import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DateRangePicker = ({ onDateChange, initialDays = 7 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('Son 7 gün');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const dropdownRef = useRef(null);

  const presets = [
    { label: 'Bugün', days: 0 },
    { label: 'Dün', days: 1 },
    { label: 'Son 7 gün', days: 7 },
    { label: 'Son 30 gün', days: 30 },
    { label: 'Bu ay', days: 'thisMonth' },
    { label: 'Geçen ay', days: 'lastMonth' },
    { label: 'Tüm zamanlar', days: 365 },
  ];

  const turkishMonths = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  const turkishDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset.label);
    let days = preset.days;
    
    if (preset.days === 'thisMonth') {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = now;
      setStartDate(start);
      setEndDate(end);
      days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    } else if (preset.days === 'lastMonth') {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(start);
      setEndDate(end);
      days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - preset.days);
      setStartDate(start);
      setEndDate(end);
    }
    
    onDateChange(typeof days === 'number' ? days : 30);
    setIsOpen(false);
    setShowCalendar(false);
  };

  const formatDateRange = () => {
    if (startDate && endDate) {
      const formatDate = (d) => `${d.getDate()} ${turkishMonths[d.getMonth()]} ${d.getFullYear()}`;
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return selectedPreset;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = (firstDay.getDay() + 6) % 7; // Monday = 0
    
    const days = [];
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonthLastDay - i) });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days;
  };

  const handleDayClick = (dayInfo) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(dayInfo.date);
      setEndDate(null);
    } else {
      if (dayInfo.date < startDate) {
        setEndDate(startDate);
        setStartDate(dayInfo.date);
      } else {
        setEndDate(dayInfo.date);
      }
    }
  };

  const isInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isSelected = (date) => {
    if (startDate && date.toDateString() === startDate.toDateString()) return true;
    if (endDate && date.toDateString() === endDate.toDateString()) return true;
    return false;
  };

  const applyCustomRange = () => {
    if (startDate && endDate) {
      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      setSelectedPreset('Özel');
      onDateChange(days || 1);
      setIsOpen(false);
      setShowCalendar(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-[#1a1a1a] border border-purple-500/30 rounded-lg text-white hover:border-purple-500 transition-colors text-sm"
      >
        <Calendar className="w-4 h-4 text-purple-500" />
        <span className="text-xs sm:text-sm whitespace-nowrap">{formatDateRange()}</span>
        <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:left-0 sm:right-auto top-auto sm:top-full mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl z-50 flex flex-col sm:flex-row max-h-[80vh] overflow-y-auto"
            style={{ bottom: 'auto' }}
          >
            {/* Preset listesi */}
            <div className="p-2 border-b sm:border-b-0 sm:border-r border-white/10 min-w-[140px]">
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-1">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handlePresetClick(preset)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors ${
                      selectedPreset === preset.label
                        ? 'bg-purple-500 text-white'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors mt-2 ${
                  showCalendar ? 'bg-purple-500 text-white' : 'bg-purple-500/20 text-purple-500 hover:bg-purple-500/30'
                }`}
              >
                Özel Tarih
              </button>
            </div>

            {/* Takvim */}
            {showCalendar && (
              <div className="p-3 min-w-[280px]">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-400" />
                  </button>
                  <span className="text-white font-medium">
                    {turkishMonths[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                  {turkishDays.map((day) => (
                    <div key={day} className="text-center text-xs text-gray-500 py-1">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(currentMonth).map((dayInfo, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleDayClick(dayInfo)}
                      className={`
                        p-2 text-sm rounded transition-colors
                        ${!dayInfo.isCurrentMonth ? 'text-gray-600' : 'text-white'}
                        ${isSelected(dayInfo.date) ? 'bg-purple-500 text-white' : ''}
                        ${isInRange(dayInfo.date) && !isSelected(dayInfo.date) ? 'bg-purple-500/20' : ''}
                        ${dayInfo.isCurrentMonth && !isSelected(dayInfo.date) ? 'hover:bg-white/10' : ''}
                      `}
                    >
                      {dayInfo.day}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => {
                      setShowCalendar(false);
                      setStartDate(null);
                      setEndDate(null);
                    }}
                    className="flex-1 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={applyCustomRange}
                    disabled={!startDate || !endDate}
                    className="flex-1 px-4 py-2 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Uygula
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DateRangePicker;
